/**
 * Presenting a floor-plan draft (10-web-admin § 5.3, § 7).
 *
 * Pure: the rebuild calendar's bars and the publish checklist are the two
 * places where an admin decides something irreversible, so what they see is
 * derived by tested functions rather than assembled in JSX.
 */

export interface RebuildDay {
  day: string;
  booked: number;
  /** `null` for days 1–7 — those are certain, not estimated. */
  expected: number | null;
}

export type BarKind = 'certain' | 'expected';

export interface CalendarBar {
  day: string;
  kind: BarKind;
  /** The solid part: what is actually booked. */
  booked: number;
  /** The light part behind it; equals `booked` on a certain day. */
  total: number;
  /** Part of a run with nothing booked and nothing expected. */
  free: boolean;
}

/** § 5.3 — one bar per day, with the free windows marked. */
export function calendarBars(
  days: readonly RebuildDay[],
  windows: ReadonlyArray<{ from: string; to: string }>,
): CalendarBar[] {
  const inWindow = (day: string): boolean => windows.some((window) => day >= window.from && day <= window.to);

  return days.map((day) => ({
    day: day.day,
    kind: day.expected === null ? 'certain' : 'expected',
    booked: day.booked,
    total: day.expected ?? day.booked,
    free: inWindow(day.day),
  }));
}

/* ------------------------------ publish view ------------------------------ */

export interface PlacementSummary {
  resourceId: string;
  label: string;
  zone: string | null;
  status: 'bookable' | 'blocked';
  xCm: number;
  yCm: number;
  wCm: number;
  hCm: number;
}

export type PublishWarning =
  /** Two desks with the same label: the sticker and the app would disagree. */
  | { kind: 'duplicate_label'; label: string; blocking: true }
  | { kind: 'overlap'; labels: [string, string]; blocking: false }
  | { kind: 'no_zone'; label: string; blocking: false }
  | { kind: 'no_scale'; blocking: false };

/**
 * § 7 — the checklist. Only a duplicate label blocks: it makes two stickers
 * that claim to be the same desk, which nothing downstream can resolve.
 */
export function publishWarnings(
  placements: readonly PlacementSummary[],
  options: { pxPerCm: number | null },
): PublishWarning[] {
  const warnings: PublishWarning[] = [];

  const byLabel = new Map<string, number>();
  for (const placement of placements) {
    byLabel.set(placement.label, (byLabel.get(placement.label) ?? 0) + 1);
  }
  for (const [label, count] of byLabel) {
    if (count > 1) warnings.push({ kind: 'duplicate_label', label, blocking: true });
  }

  for (const placement of placements) {
    if (placement.zone === null || placement.zone.trim() === '') {
      warnings.push({ kind: 'no_zone', label: placement.label, blocking: false });
    }
  }

  for (let i = 0; i < placements.length; i += 1) {
    for (let j = i + 1; j < placements.length; j += 1) {
      const a = placements[i];
      const b = placements[j];
      if (a && b && overlaps(a, b)) {
        warnings.push({ kind: 'overlap', labels: [a.label, b.label], blocking: false });
      }
    }
  }

  if (options.pxPerCm === null || options.pxPerCm <= 0) {
    warnings.push({ kind: 'no_scale', blocking: false });
  }

  return warnings;
}

function overlaps(a: PlacementSummary, b: PlacementSummary): boolean {
  return a.xCm < b.xCm + b.wCm && b.xCm < a.xCm + a.wCm && a.yCm < b.yCm + b.hCm && b.yCm < a.yCm + a.hCm;
}

/** § 7 — publishing is refused while anything blocking or unresolved is left. */
export function canPublish(input: {
  warnings: readonly PublishWarning[];
  unresolvedConflicts: number;
  changeCount: number;
}): boolean {
  if (input.changeCount === 0) return false;
  if (input.unresolvedConflicts > 0) return false;
  return !input.warnings.some((warning) => warning.blocking);
}

/** § 8 — six stickers per A4 sheet; a part-full sheet still needs printing. */
export function sheetCount(stickers: number, perSheet = 6): number {
  return Math.ceil(stickers / perSheet);
}
